import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  FormControl, InputLabel, MenuItem, Select,
  IconButton
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ResponsiveBar } from '@nivo/bar';

import { dispatch_theme } from '@/config/colors';
import { Dimension } from '@/config/types/Common';
import i18next from 'i18next';
import { extractStopsLoadV2 } from './calculateStopsWeight';
import { useSettings } from '../Settings/SettingsContext';
import { ArrowBackIosNew, ArrowForwardIos, CloseOutlined, Reply, ViewModule, WarehouseOutlined } from '@mui/icons-material';

const useStyles = makeStyles(() => ({
  chart: {
    '& svg': {
      overflow: 'visible !important',
    },
    '& svg .nivo-axis--x text': {
      fontSize: '9px !important',
      fill: '#444 !important',
      whiteSpace: 'pre-line',
    },
    '& svg .nivo-axis--y text': {
      fontSize: '14px !important',
      fill: '#222 !important',
    },
  },
  Dialogtitle: {
    background: 'rgba(239, 89, 39, 0.12)'
  },
  MainContent: {
    padding: '20px 16px !important',
    overflow: 'visible'
  },
  closeIconBtn: {
    position: 'absolute',
    right: 12,
    top: 8,
    padding: 4
  },
  dimenionLabel: {
    left: '-4px'
  },
  selectControl: {
    height: 50,
  },
  noData: {
    height: 424,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const keys = ['Occupied', 'Delivery', 'Pickup', 'Remaining'] as const;
type KeyType = typeof keys[number];

export type WeightWatcherProps = {
  route: any[] | any;
  dimensions: Dimension[];
  close: () => void;
  position: any;
  open: boolean;
  routeId: number;
  vehicle: any;
  onPrev?: () => void;
  onNext?: () => void;
};

type TooltipProps = {
  id: string | number;
  value: string | number;
  color: string;
  indexValue: string | number;
};

const WeightWatcher = (props: WeightWatcherProps) => {
  const { route, close, open, vehicle, onPrev, onNext } = props;
  const classes = useStyles();
  const { settings } = useSettings();
  const dimensions = settings.load_dimensions || [];

  const [selectedDimension, setDimension] = useState(0);

  const orderedDimensions = useMemo(() => {
    return dimensions.map((d, idx) => ({
      ...d,
      backendIndex: d.backendIndex ?? idx,
    }));
  }, [dimensions]);

  const dimensionIndex = useMemo(() => {
    const dim = orderedDimensions[selectedDimension] as any;
    if (dim && typeof dim.backendIndex === 'number') {
      return dim.backendIndex;
    }
    return Math.min(selectedDimension, 2);
  }, [orderedDimensions, selectedDimension]);

  const isADRPoints = useMemo(() => {
    const dim = orderedDimensions[selectedDimension];
    const name = (dim?.dimension || '').toLowerCase();
    return name.includes('adr');
  }, [orderedDimensions, selectedDimension]);

  const getVAxisTitle = useMemo(() => {
    const dim = orderedDimensions[selectedDimension];
    if (!dim) return '';
    return dim.unit ? `${dim.dimension} (${dim.unit})` : dim.dimension;
  }, [orderedDimensions, selectedDimension]);
  const [viewMode, setViewMode] = useState<'single' | 'multi'>('single');
  const [activeTourIdx, setActiveTourIdx] = useState(0);
  const routesArr = useMemo(() => {
    const list = Array.isArray(route) ? route : (route ? [route] : []);
    return [...list].sort((a, b) => {
      const aT = a?.start_time ? new Date(a.start_time).valueOf() : 0;
      const bT = b?.start_time ? new Date(b.start_time).valueOf() : 0;
      return aT - bT;
    });
  }, [route]);

  const activeRoute = routesArr[activeTourIdx];

  const tourNameFrom = (r: any) =>
    r?.name ?? r?.label ?? r?.route_name ?? (r?.id ? `${r.id}` : '');

  const multiTitle =
    routesArr.length <= 4
      ? routesArr.map(tourNameFrom).filter(Boolean).join(' • ')
      : `All tours (${routesArr.length})`;

  const titleText =
    viewMode === 'single'
      ? (activeRoute ? tourNameFrom(activeRoute) : '')
      : multiTitle;

  const totalTours = routesArr.length;

  const goPrev = () =>
    setActiveTourIdx(i => Math.max(0, i - 1));

  const goNext = () =>
    setActiveTourIdx(i => Math.min(totalTours - 1, i + 1));
  const toggleView = () =>
    setViewMode((m) => (m === 'single' ? 'multi' : 'single'));

  const activeTourName = routesArr[activeTourIdx]?.name || `Tour ${activeTourIdx + 1}`;

  //legend labels WITH unit suffix ---
  const legendLabels = useMemo(() => {
    const unit = orderedDimensions[selectedDimension]?.unit || '';
    const suffix = unit ? ` (${unit})` : '';
    return {
      Occupied: `${i18next.t('common:weightWatcher.occupied')}${suffix}`,
      Delivery: `${i18next.t('common:weightWatcher.delivery')}${suffix}`,
      Pickup: `${i18next.t('common:weightWatcher.pickup')}${suffix}`,
      Remaining: `${i18next.t('common:weightWatcher.remaining')}${suffix}`,
    } as Record<KeyType, string>;
  }, [orderedDimensions, selectedDimension]);

  const [barData, setBarData] = useState<any[]>([]);
  const hasData = barData.some(d => {
    const occupied = (d as any).Occupied || 0;
    const delivery = (d as any).Delivery || 0;
    const pickup = (d as any).Pickup || 0;
    return occupied > 0 || delivery > 0 || pickup > 0;
  });

  const nivoColors = [
    dispatch_theme.palette.warning.main,
    dispatch_theme.palette.info.main,
    dispatch_theme.palette.error.light,
    dispatch_theme.palette.success.main
  ];

  useEffect(() => {
    // pick routes based on view
    const usedRoutes =
      viewMode === 'single'
        ? (routesArr[activeTourIdx] ? [routesArr[activeTourIdx]] : [])
        : routesArr;

    const stopsInfo = usedRoutes.flatMap(r =>
      extractStopsLoadV2(r, dimensionIndex, isADRPoints, vehicle) || []
    );
    const round2 = (num: number) => Number(num?.toFixed(2));
    setBarData(
      (stopsInfo || []).map((stop: any, idx: number) => ({
        stopUnique: `${stop.stopName} #${idx + 1}`,
        stopLabel: stop.stopName,
        Occupied: round2(stop.utilization),
        Delivery: round2(stop.delivery),
        Pickup: round2(stop.pickup),
        Remaining: round2(stop.remainingCapacity),
      }))
    );
  }, [
    routesArr, activeTourIdx, viewMode,
    dimensionIndex, isADRPoints, vehicle
  ]);

  useEffect(() => {
    if (activeTourIdx > totalTours - 1) {
      setActiveTourIdx(Math.max(0, totalTours - 1));
    }
  }, [totalTours]);


  // CustomTooltip is now INSIDE the component and uses legendLabels
  const CustomTooltip = ({ id, value, color, indexValue }: TooltipProps) => {
    const label = legendLabels[id as KeyType] || id;
    // Find the correct label for this bar
    const found = barData.find(x => x.stopUnique === indexValue);
    const displayLabel = found?.stopLabel || indexValue;
    const isDepot = String(displayLabel).toUpperCase().includes('DEPOT');

    return (
      <div
        style={{
          background: '#fff',
          border: `1.5px solid ${color}`,
          borderRadius: 8,
          boxShadow: '0 2px 10px rgba(0,0,0,0.11)',
          padding: '8px 12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          width: 'fit-content',
          minWidth: 0,
          maxWidth: 400,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-block',
              width: 18,
              height: 18,
              borderRadius: 3,
              background: color,
              marginRight: 8,
            }}
          />
          {isDepot && (
            <WarehouseOutlined
              fontSize="small"
              sx={{ color: color, mr: 0.5 }}
            />
          )}
          <span style={{
            fontWeight: 700,
            fontSize: 13,
            color: '#111',
            marginRight: 8,
            whiteSpace: 'nowrap',
          }}>
            {label}
          </span>
          <span style={{
            fontWeight: 600,
            fontSize: 13,
            color: '#555',
            whiteSpace: 'nowrap',
          }}>
            {value}
          </span>
        </div>
        <span style={{
          fontWeight: 400,
          color: '#aaa',
          fontSize: 12,
          marginLeft: 33,
          marginTop: 2,
          wordBreak: 'break-word',
          maxWidth: 340,
        }}>
          {displayLabel}
        </span>
      </div>
    );
  };

  return (
    <Dialog open={open} maxWidth="lg" fullWidth onClose={close} data-cy="delivery-weight-dialog">
      <DialogTitle className={classes.Dialogtitle}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Grid container alignItems="center" wrap="nowrap">
              <Typography>{i18next.t('common:weightWatcher.title')}</Typography>&nbsp;
              <Typography color="primary">{titleText}</Typography>
            </Grid>
          </Grid>

          <Grid item sx={{ mr: 5 }}>
            <Grid container alignItems="center" gap={1}>
              {totalTours > 1 && (
                <>
                  <IconButton
                    size="small"
                    onClick={goPrev}
                    disabled={activeTourIdx === 0}
                    title="Previous tour"
                  >
                    <ArrowBackIosNew />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={goNext}
                    disabled={activeTourIdx === totalTours - 1}
                    title="Next tour"
                  >
                    <ArrowForwardIos />
                  </IconButton>
                </>
              )}
              <IconButton
                aria-label="close"
                onClick={close}
                className={classes.closeIconBtn}
              >
                <CloseOutlined style={{ color: '#000000' }} />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      </DialogTitle>
      {open && (
        <DialogContent className={classes.MainContent}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            wrap="nowrap"
          >
            <Grid item>
              <FormControl fullWidth style={{ width: '190px' }}>
                <InputLabel id="dimension-label" className={classes.dimenionLabel}>
                  {i18next.t('common:weightWatcher.dimenionslabel')}
                </InputLabel>
                <Select
                  className={classes.selectControl}
                  labelId="dimension-label"
                  id="dimension-select"
                  value={selectedDimension}
                  onChange={(e) => setDimension(Number(e.target.value))}
                  label="Select Dimension"
                  size="small"
                >
                  {orderedDimensions.map((dim, i) => (
                    <MenuItem value={i} key={i}>
                      {dim.dimension}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Grid container spacing={1} alignItems="center" wrap="nowrap">
                {keys.map((key, idx) => (
                  <Grid item key={key} style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 18,
                        height: 18,
                        borderRadius: 3,
                        background: nivoColors[idx],
                        marginRight: 8,
                      }}
                    />
                    <Typography variant="body2" style={{ marginRight: 18, whiteSpace: 'nowrap' }}>
                      {legendLabels[key]}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          <Grid container justifyContent="center">
            {!hasData ? (
              <div
                style={{
                  filter: 'grayscale(100%)',
                  opacity: 0.5,
                  width: '100%',
                  textAlign: 'center',
                  padding: 40
                }}
              >
                <div className={classes.noData}>
                  <Typography variant="h6">
                    {i18next.t('common:weightWatcher.noData')}
                  </Typography>
                </div>
              </div>
            ) : (
              <div
                className={classes.chart}
                style={{
                  height: 519,
                  width: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  cursor: 'pointer'
                }}
              >
                <ResponsiveBar
                  data={barData}
                  keys={keys}
                  indexBy="stopUnique"
                  margin={{ top: 40, right: 30, bottom: 120, left: 80 }}
                  padding={0.23}
                  groupMode="stacked"
                  colors={nivoColors}
                  axisBottom={{
                    tickRotation: -30,
                    legend: '',
                    legendOffset: 44,
                    legendPosition: 'middle',
                    tickPadding: 4,
                    format: (d) => {
                      const found = barData.find(x => x.stopUnique === d);
                      if (!found) return String(d);
                      if (found.stopLabel.toUpperCase().startsWith('DEPOT')) return 'DEPOT';
                      return found.stopLabel;
                    },
                  }}
                  axisLeft={{
                    legend: getVAxisTitle,
                    legendPosition: 'middle',
                    legendOffset: -60,
                  }}
                  legends={[]}
                  labelSkipWidth={20}
                  labelSkipHeight={15}
                  labelTextColor="#fff"
                  animate
                  tooltip={CustomTooltip}
                  theme={{
                    background: 'transparent',
                    text: { fill: '#333', fontWeight: 500, fontSize: 13 },
                    axis: {
                      legend: {
                        text: {
                          fontSize: 14,
                          fill: '#222',
                          fontFamily: 'sans-serif',
                        },
                      },
                    },
                    tooltip: {
                      container: {
                        background: '#fff',
                        color: '#222',
                        fontSize: 16,
                        borderRadius: 10,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.20)',
                        padding: '16px 22px',
                        border: 'none'
                      },
                    },
                  }}
                  role="application"
                  ariaLabel="Weight Bar Chart"
                />
              </div>
            )}
          </Grid>

        </DialogContent>
      )}
    </Dialog>
  );
};

export default WeightWatcher;
